/**
 * @author: lv.chen
 * @date: 2025/3/6
 * @desc: GR Action Item编辑组件
 */
import GRActionItemsFormControl from '@gp/components/GRActionItemsFormControl';
import GeneralImageNew from '@gp/components/GeneralImageNew';
import GeneralModal from '@gp/components/GeneralModal';
import Loading from '@gp/components/Loading';
import { CONSTANT_TEXTS, FIELD_COMMON_ERR_MSG } from '@gp/constants/const';
import { useAutoSaveParams } from '@gp/hooks/useAutoSaveParams';
import type { IGRAction } from '@gp/pages/Pipeline/ProjectDetails/Milestone/types';
import { getIsNotEmptyList } from '@gp/pages/Pipeline/ProjectDetails/Milestone/utils';
import { updateProjectGRActionItemsProject } from '@gp/services/grActionItems';
import type { IProjectGRActionItemProjectDTO } from '@gp/types/grActionItems';
import {
  addIdBatch,
  formatTeaFormItemMessage,
  formatTeaFormItemStatus,
  reportBtnOperationLog,
} from '@gp/utils/common';
import type { IBaseProps } from '@home/types/common';
import { isEmptyValue } from '@home/utils/common';
import classnames from 'classnames';
import { isEqual } from 'lodash-es';
import React, { useEffect, useMemo, useState } from 'react';
import { useField, useForm } from 'react-final-form-hooks';
import { Form } from 'tea-component';
import useFetchGRActionItems from './_hooks/useFetchGRActionItems';

import './index.scss';

export interface IEditGRActionItemsFormModel {
  gr_action_items: IGRAction[];
}

interface IEditGRActionItemsFormModalProps extends IBaseProps {
  visible?: boolean;
  setVisible?: React.Dispatch<React.SetStateAction<boolean>>;
  data?: IProjectGRActionItemProjectDTO | null;
  onEditSuccess?: () => void;
  onEditCancel?: () => void;
}

const DEFAULT_FORM_MODEL: IEditGRActionItemsFormModel = {
  gr_action_items: [],
};

const handleValidate = (values?: IEditGRActionItemsFormModel): Record<string, string> => {
  if (!values) return { gr_action_items: FIELD_COMMON_ERR_MSG };
  const errors: Record<string, string> = {};
  const grActionItemsValue = values?.gr_action_items;
  const isGRActionItemsValid =
    !Array.isArray(grActionItemsValue) ||
    grActionItemsValue?.every((grActionItem) => !isEmptyValue(grActionItem?.due_date));
  if (!isGRActionItemsValid) {
    errors.gr_action_items = FIELD_COMMON_ERR_MSG;
  }
  return errors;
};

const getIsFormAvailable = (values?: IEditGRActionItemsFormModel): boolean => {
  const { gr_action_items: grActionItems } = values || {};
  if (Array.isArray(grActionItems) && grActionItems.length === 0) return true;
  return getIsNotEmptyList(grActionItems);
};

const EditGRActionItemsFormModal: React.FC<IEditGRActionItemsFormModalProps> = (props) => {
  const {
    style,
    className,
    visible = false,
    setVisible,
    data,
    onEditSuccess,
    pageKeyCur,
    isUseAutoSaveDatas,
    onEditCancel,
    actionItemCategory,
  } = props;

  const {
    project_id: projectId,
    project,
    studio_id: studioId,
    cover,
    gate_review: gateReview,
  } = data || {};
  const { milestone_id: milestoneId, milestone } = gateReview || {};

  const { grActionItemsLoading, grActionItems } = useFetchGRActionItems({
    execute: visible,
    projectId,
    milestoneId,
  });

  const [submitted, setSubmitted] = useState(false);
  const [isChangeValue, setIsChangeValue] = useState<boolean>(false);
  const handleConfirm = async (values: IEditGRActionItemsFormModel) => {
    try {
      await updateProjectGRActionItemsProject({
        project_id: projectId || '',
        studio_id: studioId || '',
        milestone_id: milestoneId || '',
        action_item_list: values?.gr_action_items || [],
      });
      setSubmitted(false);
      form?.restart();
      onEditSuccess?.();
      setVisible?.(false);
    } catch (exception) {
    } finally {
      reportBtnOperationLog('PIPELINE_GR_ACTION_ITEMS_EDIT_SUBMIT', { extInfo: projectId || '' });
    }
  };

  const handleSubmitInPage = () => {
    setSubmitted(true);
    handleSubmit?.();
  };

  const handleCancel = () => {
    onEditCancel?.();
    setVisible?.(false);
    setSubmitted(false);
    form?.restart();
  };

  const { form, values, submitting, handleSubmit } = useForm({
    initialValues: DEFAULT_FORM_MODEL,
    onSubmit: handleConfirm,
    validate: handleValidate,
  });

  const grActionItemsField = useField('gr_action_items', form);

  const formAvailable = useMemo<boolean>(() => getIsFormAvailable(values), [values]);

  const pristine = useMemo<boolean>(() => {
    // @ts-ignore
    const commonsPristine = [].every((field) => !!field?.meta?.pristine);

    const specialsPristine = [{ key: 'gr_action_items', field: grActionItemsField }].every(
      (meta) => {
        const { key, field } = meta || {};
        return isEqual(values?.[key], field?.meta?.initial);
      },
    );

    return commonsPristine && specialsPristine;
  }, [grActionItemsField, values]);

  useEffect(() => {
    if (grActionItemsLoading) return;
    const saveActionItems = data?.gr_action_items || [];
    form?.initialize({
      ...DEFAULT_FORM_MODEL,
      gr_action_items: addIdBatch(isUseAutoSaveDatas ? saveActionItems : grActionItems) || [],
    });
  }, [grActionItemsLoading, grActionItems, data, isUseAutoSaveDatas, form]);

  const saveParamsCur = useMemo(() => {
    return {
      ...data,
      gr_action_items: values?.gr_action_items,
    };
  }, [data, values]);

  // 每隔5秒自动保存
  useAutoSaveParams({
    pageKey: pageKeyCur,
    initialParams: data,
    saveParamsCur,
    isBegin: visible,
    isChangeValue,
  });

  return (
    <GeneralModal
      style={style}
      className={classnames('dbg-pipeline-gr-action-item-edit-modal', className)}
      title={CONSTANT_TEXTS.GR_ACTION_ITEMS}
      modalSize={1320}
      isFont14={true}
      visible={visible}
      disabledConfirm={isUseAutoSaveDatas ? false : !formAvailable || pristine}
      loadingConfirm={submitting}
      onConfirm={handleSubmitInPage}
      onClose={handleCancel}
      content={
        <Form
          className="gp-form dbg-pipeline-gr-action-item-edit-form"
          layout="fixed"
          fixedLabelWidth={120}
        >
          <Form.Item className="dbg-pipeline-gr-action-item-project" label="Project">
            <div className="dbg-pipeline-gr-action-item-edit-form__project-summary">
              <GeneralImageNew
                className="dbg-pipeline-gr-action-item-edit-form__project-summary-cover"
                domain="game"
                width={34}
                height={34}
                src={cover || ''}
              />
              <span className="dbg-pipeline-gr-action-item-edit-form__project-summary-text">
                {project || '-'}
              </span>
            </div>
          </Form.Item>
          <Form.Item
            showStatusIcon={false}
            status={formatTeaFormItemStatus(submitted, grActionItemsField.meta)}
            message={formatTeaFormItemMessage(submitted, grActionItemsField.meta)}
            className="dbg-pipeline-gr-action-item-add-items"
            label={
              <>
                {CONSTANT_TEXTS.GR_ACTION_ITEMS}
                <br />
                {!!milestone && <span>({milestone})</span>}
              </>
            }
          >
            {grActionItemsLoading && (
              <div className="dbg-gr-action-items__state-wrapper dbg-gr-action-items__loading">
                <Loading loading />
              </div>
            )}
            {grActionItemsLoading || (
              <GRActionItemsFormControl
                {...grActionItemsField.input}
                setIsChangeValue={setIsChangeValue}
                actionItemCategory={actionItemCategory}
              />
            )}
          </Form.Item>
        </Form>
      }
    />
  );
};

export default EditGRActionItemsFormModal;
